import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from '@tanstack/react-router';
import DOMPurify from 'dompurify';
import { ArrowLeft, Trash2, Download } from 'lucide-react';
import { Skeleton } from '../shared/ui/Skeleton';
import { toast } from '../shared/ui/toast';
import { formatDate, formatBytes } from '../shared/lib/format';
import type { Attachment } from '../shared/api/types';
import {
  useAttachmentDownload,
  useDeleteMessage,
  useDownloadAttachmentsArchive,
  useInlineAttachmentUrls,
  useMessage,
} from '../features/message/api';
import { attachmentIcon } from '../features/message/lib';

export function MessageDetailPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { mailboxId, messageId } = useParams({
    from: '/mailbox/$mailboxId/message/$messageId',
  });

  const messageQuery = useMessage(messageId);
  const del = useDeleteMessage(mailboxId);
  const download = useAttachmentDownload();
  const downloadArchive = useDownloadAttachmentsArchive();
  const m = messageQuery.data;

  const inlineUrls = useInlineAttachmentUrls(messageId, m?.attachments).data;

  // Встроенные картинки — часть тела письма; в списке вложений их не показываем.
  const files = useMemo(() => (m?.attachments ?? []).filter((a) => !a.isInline), [m?.attachments]);

  const safeHtml = useMemo(
    () => (m?.bodyHtml ? resolveInlineImages(DOMPurify.sanitize(m.bodyHtml), inlineUrls) : null),
    [m?.bodyHtml, inlineUrls]
  );

  const back = () => void navigate({ to: '/mailbox/$mailboxId', params: { mailboxId } });

  const onDownload = (a: Attachment) => {
    download.mutate(a.id, {
      onSuccess: (res) => {
        window.open(res.url, '_blank', 'noopener');
      },
      onError: () => toast.error(t('common.error')),
    });
  };

  const onDownloadAll = () => {
    downloadArchive.mutate(messageId, {
      onError: () => toast.error(t('common.error')),
    });
  };

  return (
    <div className="space-y-4">
      <button
        type="button"
        onClick={back}
        className="flex items-center gap-2 text-sm text-fg-muted hover:text-fg"
      >
        <ArrowLeft className="h-4 w-4" /> {t('app.back')}
      </button>

      {messageQuery.isLoading || !m ? (
        <Skeleton className="h-64 w-full rounded-2xl" />
      ) : (
        <article className="rounded-2xl bg-surface p-4 shadow-card sm:p-6">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="truncate font-semibold text-fg">{m.fromName ?? m.fromAddress}</p>
              <p className="mt-0.5 text-sm text-fg-muted">{formatDate(m.receivedAt)}</p>
            </div>
            <button
              type="button"
              aria-label={t('message.detail.delete')}
              onClick={() => {
                del.mutate(m.id, {
                  onSuccess: () => {
                    toast.success(t('message.toasts.deleted'));
                    back();
                  },
                });
              }}
              className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-brand-soft text-brand hover:bg-brand-softhover"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </div>

          <h1 className="mt-4 break-words text-lg font-bold text-fg">
            {m.subject ?? t('message.detail.noSubject')}
          </h1>

          {safeHtml ? (
            <div
              className="prose mt-3 max-w-none overflow-x-auto break-words text-sm text-fg [&_a]:text-brand [&_img]:h-auto [&_img]:max-w-full [&_table]:w-full"
              // санитизация через DOMPurify выше — XSS-safe (ТЗ §10.5)
              dangerouslySetInnerHTML={{ __html: safeHtml }}
            />
          ) : (
            <p className="mt-3 whitespace-pre-wrap break-words text-sm text-fg">{m.bodyText}</p>
          )}

          {files.length > 0 && (
            <div className="mt-6">
              <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                <h2 className="font-semibold text-fg">{t('message.detail.attachmentsTitle')}</h2>
                {files.length > 1 && (
                  <button
                    type="button"
                    onClick={onDownloadAll}
                    disabled={downloadArchive.isPending}
                    className="flex items-center gap-1.5 text-sm font-medium text-brand disabled:opacity-50"
                  >
                    <Download className="h-4 w-4" /> {t('message.detail.downloadAll')}
                  </button>
                )}
              </div>
              <div className="grid gap-2 sm:grid-cols-2">
                {files.map((a) => {
                  const Icon = attachmentIcon(a.fileName, a.contentType);
                  return (
                    // min-w-0 нужен на самом grid-элементе: у него `min-width: auto`, и без
                    // сброса дорожка растягивается под полное имя файла, унося иконку скачивания
                    // за границу карточки — `truncate` внутри при этом не срабатывает (promt-86 §3.1).
                    <div key={a.id} className="flex min-w-0 items-center gap-3 rounded-xl border border-border p-3">
                      <span className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-accent-soft text-accent-text">
                        <Icon className="h-4 w-4" />
                      </span>
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm text-fg" title={a.fileName}>
                          {a.fileName}
                        </p>
                        <p className="text-xs text-fg-muted">{formatBytes(a.sizeBytes)}</p>
                      </div>
                      <button
                        type="button"
                        aria-label={t('message.detail.download')}
                        onClick={() => {
                          onDownload(a);
                        }}
                        className="grid h-8 w-8 shrink-0 place-items-center rounded-lg text-fg-muted hover:bg-page hover:text-fg"
                      >
                        <Download className="h-4 w-4" />
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </article>
      )}
    </div>
  );
}

/**
 * Подставляет presigned-URL'ы вместо ссылок `src="cid:..."` в уже санитизированном HTML,
 * чтобы встроенные картинки отображались в теле письма (RFC 2392).
 */
function resolveInlineImages(html: string, urls: Record<string, string> | undefined): string {
  if (!urls || !html.includes('cid:')) return html;

  const doc = new DOMParser().parseFromString(html, 'text/html');
  for (const img of doc.querySelectorAll('img[src^="cid:"]')) {
    const cid = decodeURIComponent((img.getAttribute('src') ?? '').slice('cid:'.length));
    const url = urls[cid];
    if (url) img.setAttribute('src', url);
  }
  return doc.body.innerHTML;
}
